export type AuditTitleEvent = {
  user_name?: string | null;
  event_type?: string | null;
  entity_name?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  action_description?: string | null;
  timestamp?: string | null;
  details?: Record<string, unknown> | null;
};

const VERBS: Record<string, string> = {
  login: 'signed in',
  create: 'created',
  update: 'updated',
  delete: 'deleted',
  approve: 'approved',
  reject: 'rejected',
  submit: 'submitted',
  review: 'reviewed',
  delegate: 'delegated',
  complete: 'completed',
  revoke: 'revoked',
};

export function auditStoredPath(event: AuditTitleEvent): string {
  const path = String(event.details?.path || '').trim();
  if (path) return path;
  const url = String(event.details?.url || '').trim();
  if (!url) return '';
  try {
    const parsed = new URL(url, 'http://localhost');
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

function pathOf(event: AuditTitleEvent): string {
  return auditStoredPath(event);
}

export function isSignInEvent(event: AuditTitleEvent): boolean {
  const description = event.action_description || '';
  return event.event_type === 'login'
    || /\/auth\/login\b/i.test(pathOf(event))
    || /\bsigned in\b/i.test(description);
}

export function isSecretAccessEvent(event: AuditTitleEvent): boolean {
  const blob = `${pathOf(event)} ${event.action_description || ''} ${event.entity_type || ''}`;
  return /secret|api[_ -]?token|password|provider/i.test(blob);
}

export function isBulkReadEvent(event: AuditTitleEvent): boolean {
  const method = String(event.details?.method || '');
  return method === 'GET' || event.event_type === 'review';
}

export function auditHumanTitle(event: AuditTitleEvent): string {
  const actor = (event.user_name || 'Someone').trim() || 'Someone';
  if (isSignInEvent(event)) return `${actor} signed in`;
  const verb = VERBS[event.event_type || ''] || String(event.event_type || 'changed').replace(/_/g, ' ');
  const target = String(event.entity_name || event.entity_type || 'a record').replace(/_/g, ' ');
  return `${actor} ${verb} ${target}`;
}

/** Lower ranks stay above bulk reads when events share a minute. */
export function auditRank(event: AuditTitleEvent): number {
  if (isSignInEvent(event)) return 0;
  if (isSecretAccessEvent(event)) return 1;
  if (isBulkReadEvent(event)) return 3;
  return 2;
}

export function sortAuditEvents<T extends AuditTitleEvent>(events: T[]): T[] {
  return [...events].sort((left, right) => {
    const leftMinute = Math.floor(new Date(left.timestamp || 0).getTime() / 60000);
    const rightMinute = Math.floor(new Date(right.timestamp || 0).getTime() / 60000);
    if (leftMinute !== rightMinute) return rightMinute - leftMinute;
    const rank = auditRank(left) - auditRank(right);
    if (rank !== 0) return rank;
    return new Date(right.timestamp || 0).getTime() - new Date(left.timestamp || 0).getTime();
  });
}

export function auditRecordPath(event: AuditTitleEvent): string | null {
  const id = event.entity_id;
  if (!id) return null;
  if (event.entity_type === 'user') return `/user-profile/${id}`;
  if (event.entity_type === 'application') return `/applications`;
  if (event.entity_type === 'sso_config') return `/sso-review?config=${id}`;
  if (event.entity_type === 'pam_account') return '/pam-dashboard';
  return null;
}
